import { DropdownItem, TableRowValueList } from '@vinnova/ui';
import { Worksite } from '../../../../core/models/question/question-worksite.model';
import { Finansieringstyp, Kostnadstyp } from '../budget.interface';
import {
  BUDGET_IMPORT_ROW_SOKT_BIDRAG,
  BUDGET_IMPORT_UNIT_FACTOR,
  BudgetImportApplyProjektpart,
  BudgetImportCell,
  BudgetImportProjektpart,
  BudgetImportResult,
  BudgetImportUnit,
  BudgetSlotSchema,
} from './budget-import.interface';
import { BUDGET_IMPORT_TEXT, KOSTNADSSLAG_LABELS } from './budget-import.text';

/** Slot value for a file row the user does not want to import. */
export const BUDGET_IMPORT_SLOT_NONE = 'none';

/** Slot the user picked per source row key; rows without an entry use the suggestion from the analysis. */
export type BudgetImportRowAssignments = Record<string, string>;

export interface BudgetImportPreviewCell {
  ar: number;
  current: number;
  /** Scaled to SEK; undefined when the import had no value for this slot. */
  imported?: number;
  source?: string;
}

export interface BudgetImportPreviewRow {
  id: string;
  rowType?: number;
  finansiarId?: string;
  label: string;
  cells: BudgetImportPreviewCell[];
}

/** One row read from the file, with the slot it is currently placed in. */
export interface BudgetImportPreviewSourceRow {
  key: string;
  label: string;
  source?: string;
  /** Scaled to SEK. */
  amounts: Record<number, number | undefined>;
  slot: string;
  /** Label of the suggested slot when the suggestion was withheld because several rows suggested it. */
  withheldSuggestionLabel?: string;
  /** Other rows placed in the same slot; the client sums them. */
  sharesSlotWith: number;
}

export interface BudgetImportPreviewPart {
  part: BudgetImportProjektpart;
  kostnader: BudgetImportPreviewRow[];
  finansiering: BudgetImportPreviewRow[];
  hasImportedValues: boolean;
  sourceRows: BudgetImportPreviewSourceRow[];
  slotOptions: DropdownItem[];
}

interface SourceRow {
  key: string;
  label: string;
  source?: string;
  cells: BudgetImportCell[];
  suggestedSlot: string;
}

interface RowPlacement {
  slot: string;
  withheldSuggestionLabel?: string;
}

/** Current table values per worksite id, in the same shape the budget component keeps in its form controls. */
export interface CurrentBudgetTables {
  kostnader: TableRowValueList;
  finansiering: TableRowValueList;
}

export class BudgetImportHelper {
  static scale(summa: number, unit: BudgetImportUnit): number {
    return Math.round(summa * (BUDGET_IMPORT_UNIT_FACTOR[unit] ?? 1));
  }

  static buildSchema(
    startDate: Date,
    endDate: Date,
    koordinator: Worksite,
    projektparter: Worksite[],
  ): BudgetSlotSchema {
    const toPart = (
      worksite: Worksite,
      koordinerande: boolean,
    ): BudgetImportProjektpart => ({
      id: worksite.id,
      koordinerande,
      namn: worksite.company?.name ?? worksite.name,
      arbetsstalleNamn: worksite.name,
      organisationsnummer: worksite.company?.nationalRegistrationNumber,
      adress: BudgetImportHelper.buildAdress(worksite),
      finansiarer: (worksite.financiers ?? []).map((f) => ({
        id: f.id,
        namn: f.company?.name ?? f.name,
      })),
    });

    return {
      startYear: startDate.getFullYear(),
      endYear: endDate.getFullYear(),
      projektparter: [
        toPart(koordinator, true),
        ...projektparter.map((p) => toPart(p, false)),
      ],
    };
  }

  /** Street, zip and city combined so a workbook caption naming only the city can still match. */
  private static buildAdress(worksite: Worksite): string | undefined {
    const street = worksite.address ?? worksite.company?.address;
    const zipcode = worksite.zipcode ?? worksite.company?.zipcode;
    const city = worksite.city ?? worksite.company?.city;
    return (
      [street, [zipcode, city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ') || undefined
    );
  }

  static years(
    result: Pick<BudgetImportResult, 'startYear' | 'endYear'>,
  ): number[] {
    const years: number[] = [];
    for (let year = result.startYear; year <= result.endYear; year++) {
      years.push(year);
    }
    return years;
  }

  static buildPreview(
    result: BudgetImportResult,
    unit: BudgetImportUnit,
    current: Map<string, CurrentBudgetTables>,
    assignments: BudgetImportRowAssignments = {},
  ): BudgetImportPreviewPart[] {
    const years = BudgetImportHelper.years(result);
    const cellsByPart = new Map<string, BudgetImportCell[]>();
    for (const cell of result.cells) {
      const list = cellsByPart.get(cell.projektpartId) ?? [];
      list.push(cell);
      cellsByPart.set(cell.projektpartId, list);
    }

    return result.projektparter.map((part) => {
      const tables = current.get(part.id);
      const slotOptions = BudgetImportHelper.buildSlotOptions(part);
      const sourceRows = BudgetImportHelper.groupSourceRows(
        part.id,
        cellsByPart.get(part.id) ?? [],
      );
      const placements = BudgetImportHelper.placeRows(
        sourceRows,
        assignments,
        slotOptions,
      );
      const cells = BudgetImportHelper.sumPlacedCells(
        part.id,
        sourceRows,
        placements,
      );

      const kostnader = Object.values(Kostnadstyp).map((kostnadstyp) =>
        BudgetImportHelper.buildRow(
          { rowType: Number(kostnadstyp) },
          kostnadstyp,
          KOSTNADSSLAG_LABELS[kostnadstyp],
          years,
          cells,
          tables?.kostnader ?? [],
          result.startYear,
          unit,
        ),
      );
      const finansiering = [
        BudgetImportHelper.buildRow(
          { rowType: BUDGET_IMPORT_ROW_SOKT_BIDRAG },
          Finansieringstyp.VINNOVABIDRAG,
          BUDGET_IMPORT_TEXT.soktBidrag,
          years,
          cells,
          tables?.finansiering ?? [],
          result.startYear,
          unit,
        ),
        ...(part.finansiarer ?? []).map((finansiar) =>
          BudgetImportHelper.buildRow(
            { finansiarId: finansiar.id },
            finansiar.id,
            finansiar.namn ?? '',
            years,
            cells,
            tables?.finansiering ?? [],
            result.startYear,
            unit,
          ),
        ),
      ];

      return {
        part,
        kostnader,
        finansiering,
        hasImportedValues: [...kostnader, ...finansiering].some((row) =>
          row.cells.some((cell) => cell.imported !== undefined),
        ),
        sourceRows: sourceRows.map((row) => {
          const placement = placements.get(row.key) ?? {
            slot: BUDGET_IMPORT_SLOT_NONE,
          };
          return {
            key: row.key,
            label: row.label,
            source: row.source,
            amounts: Object.fromEntries(
              years.map((ar) => {
                const cell = row.cells.find((c) => c.ar === ar);
                return [
                  ar,
                  cell ? BudgetImportHelper.scale(cell.summa, unit) : undefined,
                ];
              }),
            ),
            slot: placement.slot,
            withheldSuggestionLabel: placement.withheldSuggestionLabel,
            sharesSlotWith:
              placement.slot === BUDGET_IMPORT_SLOT_NONE
                ? 0
                : [...placements.values()].filter(
                    (p) => p.slot === placement.slot,
                  ).length - 1,
          };
        }),
        slotOptions,
      };
    });
  }

  /** Where a file row can be placed: nothing, one of the kostnadsslag, sökt bidrag, or one of the part's financiers. */
  private static buildSlotOptions(
    part: BudgetImportProjektpart,
  ): DropdownItem[] {
    return [
      { value: BUDGET_IMPORT_SLOT_NONE, label: BUDGET_IMPORT_TEXT.rowsIgnore },
      ...Object.values(Kostnadstyp).map((kostnadstyp) => ({
        value: kostnadstyp,
        label: KOSTNADSSLAG_LABELS[kostnadstyp],
      })),
      {
        value: BUDGET_IMPORT_ROW_SOKT_BIDRAG.toString(),
        label: BUDGET_IMPORT_TEXT.soktBidrag,
      },
      ...(part.finansiarer ?? []).map((finansiar) => ({
        value: finansiar.id,
        label: finansiar.namn ?? '',
      })),
    ];
  }

  private static slotOf(cell: BudgetImportCell): string {
    return cell.rowType === undefined || cell.rowType === null
      ? (cell.finansiarId ?? '')
      : cell.rowType.toString();
  }

  /** One entry per source row of the file; cells without a row key each count as their own row. */
  private static groupSourceRows(
    partId: string,
    cells: BudgetImportCell[],
  ): SourceRow[] {
    const rows = new Map<string, SourceRow>();
    for (const [index, cell] of cells.entries()) {
      const rowId = cell.rowKey ?? 'cell-' + index;
      const key = partId + '|' + rowId;
      let row = rows.get(key);
      if (!row) {
        row = {
          key,
          label: cell.label ?? cell.sourceCell ?? '',
          cells: [],
          suggestedSlot: '',
        };
        rows.set(key, row);
      }
      row.cells.push(cell);
      row.suggestedSlot ||= BudgetImportHelper.slotOf(cell);
    }

    return [...rows.values()].map((row) => {
      const refs = row.cells.map((c) => c.sourceCell).filter(Boolean);
      const sheet = row.cells.find((c) => c.sourceSheet)?.sourceSheet;
      return {
        ...row,
        source:
          refs.length > 0
            ? `${sheet ? sheet + '!' : ''}${refs.join(', ')}`
            : undefined,
      };
    });
  }

  /**
   * Starts from the analysis' suggestions, but when several rows suggest the same slot none of them is placed:
   * combining rows is the user's decision, never made for them.
   */
  private static placeRows(
    rows: SourceRow[],
    assignments: BudgetImportRowAssignments,
    slotOptions: DropdownItem[],
  ): Map<string, RowPlacement> {
    const labels = new Map(slotOptions.map((o) => [o.value, o.label]));
    const suggestionCount = new Map<string, number>();
    for (const row of rows) {
      if (row.suggestedSlot) {
        suggestionCount.set(
          row.suggestedSlot,
          (suggestionCount.get(row.suggestedSlot) ?? 0) + 1,
        );
      }
    }

    return new Map(
      rows.map((row): [string, RowPlacement] => {
        const chosen = assignments[row.key];
        if (chosen !== undefined && labels.has(chosen)) {
          return [row.key, { slot: chosen }];
        }
        const suggested = labels.has(row.suggestedSlot)
          ? row.suggestedSlot
          : '';
        if (suggested && suggestionCount.get(suggested) === 1) {
          return [row.key, { slot: suggested }];
        }
        return [
          row.key,
          {
            slot: BUDGET_IMPORT_SLOT_NONE,
            withheldSuggestionLabel: suggested
              ? labels.get(suggested)
              : undefined,
          },
        ];
      }),
    );
  }

  /** The one place amounts are added up: cells of all rows placed in the same slot and year. */
  private static sumPlacedCells(
    partId: string,
    rows: SourceRow[],
    placements: Map<string, RowPlacement>,
  ): BudgetImportCell[] {
    const sums = new Map<string, BudgetImportCell>();
    for (const row of rows) {
      const slot = placements.get(row.key)?.slot ?? BUDGET_IMPORT_SLOT_NONE;
      if (slot === BUDGET_IMPORT_SLOT_NONE) {
        continue;
      }
      for (const cell of row.cells) {
        const sumKey = `${slot}|${cell.ar}`;
        const existing = sums.get(sumKey);
        if (existing) {
          sums.set(sumKey, {
            ...existing,
            summa: existing.summa + cell.summa,
            sourceSheet: undefined,
            sourceCell: [
              BudgetImportHelper.sourceRef(existing),
              BudgetImportHelper.sourceRef(cell),
            ]
              .filter(Boolean)
              .join(' + '),
          });
          continue;
        }
        const isFixedRow = Number.isInteger(Number(slot));
        sums.set(sumKey, {
          projektpartId: partId,
          rowType: isFixedRow ? Number(slot) : undefined,
          finansiarId: isFixedRow ? undefined : slot,
          ar: cell.ar,
          summa: cell.summa,
          sourceSheet: cell.sourceSheet,
          sourceCell: cell.sourceCell,
        });
      }
    }
    return [...sums.values()];
  }

  private static sourceRef(cell: BudgetImportCell): string | undefined {
    return cell.sourceCell
      ? `${cell.sourceSheet ? cell.sourceSheet + '!' : ''}${cell.sourceCell}`
      : undefined;
  }

  /** Turns confirmed preview parts into per-part patches, dropping cells the import left empty. */
  static toApplyParts(
    preview: Omit<BudgetImportPreviewPart, 'sourceRows' | 'slotOptions'>[],
  ): BudgetImportApplyProjektpart[] {
    return preview.map((p) => {
      const kostnader: Record<string, Record<number, number>> = {};
      for (const row of p.kostnader) {
        for (const cell of row.cells) {
          if (cell.imported === undefined) {
            continue;
          }
          const rowId = row.rowType?.toString() ?? row.id;
          kostnader[rowId] ??= {};
          kostnader[rowId][cell.ar] = cell.imported;
        }
      }
      const bidrag: Record<number, number> = {};
      for (const cell of p.finansiering[0]?.cells ?? []) {
        if (cell.imported !== undefined) {
          bidrag[cell.ar] = cell.imported;
        }
      }
      const finansiarer: Record<string, Record<number, number>> = {};
      for (const row of p.finansiering) {
        if (!row.finansiarId) {
          continue;
        }
        for (const cell of row.cells) {
          if (cell.imported === undefined) {
            continue;
          }
          finansiarer[row.finansiarId] ??= {};
          finansiarer[row.finansiarId][cell.ar] = cell.imported;
        }
      }
      return { id: p.part.id, kostnader, bidrag, finansiarer };
    });
  }

  /** Overwrites only the cells present in the patch; other rows are untouched. */
  static mergeIntoTables(
    tables: CurrentBudgetTables,
    patch: BudgetImportApplyProjektpart,
    startYear: number,
  ): CurrentBudgetTables {
    const kostnader = [...tables.kostnader];
    for (const [rowId, perYear] of Object.entries(patch.kostnader)) {
      for (const [year, value] of Object.entries(perYear)) {
        BudgetImportHelper.upsert(
          kostnader,
          rowId,
          (Number(year) - startYear).toString(),
          value,
        );
      }
    }

    const finansiering = [...tables.finansiering];
    for (const [year, value] of Object.entries(patch.bidrag)) {
      BudgetImportHelper.upsert(
        finansiering,
        Finansieringstyp.VINNOVABIDRAG,
        (Number(year) - startYear).toString(),
        value,
      );
    }
    for (const [finansiarId, perYear] of Object.entries(patch.finansiarer)) {
      for (const [year, value] of Object.entries(perYear)) {
        BudgetImportHelper.upsert(
          finansiering,
          finansiarId,
          (Number(year) - startYear).toString(),
          value,
        );
      }
    }

    return { kostnader, finansiering };
  }

  private static buildRow(
    key: { rowType: number } | { finansiarId: string },
    currentRowId: string,
    label: string,
    years: number[],
    cells: BudgetImportCell[],
    currentTable: TableRowValueList,
    startYear: number,
    unit: BudgetImportUnit,
  ): BudgetImportPreviewRow {
    const matches = (cell: BudgetImportCell) =>
      'rowType' in key
        ? cell.rowType === key.rowType
        : cell.finansiarId === key.finansiarId;

    return {
      id: 'rowType' in key ? key.rowType.toString() : key.finansiarId,
      rowType: 'rowType' in key ? key.rowType : undefined,
      finansiarId: 'finansiarId' in key ? key.finansiarId : undefined,
      label,
      cells: years.map((ar) => {
        const imported = cells.find((c) => matches(c) && c.ar === ar);
        const current = currentTable.find(
          (c) =>
            c.rowId === currentRowId &&
            c.columnId === (ar - startYear).toString(),
        );
        return {
          ar,
          current: current?.value ?? 0,
          imported: imported
            ? BudgetImportHelper.scale(imported.summa, unit)
            : undefined,
          source: imported && BudgetImportHelper.sourceRef(imported),
        };
      }),
    };
  }

  private static upsert(
    table: TableRowValueList,
    rowId: string,
    columnId: string,
    value: number,
  ): void {
    const index = table.findIndex(
      (c) => c.rowId === rowId && c.columnId === columnId,
    );
    if (index === -1) {
      table.push({ rowId, columnId, value });
    } else {
      table[index] = { ...table[index], value };
    }
  }
}
